/**
 * Shared responsive layout primitives for the activity components (§13).
 *
 * - Container fills the host column and inherits its max-width (the feed caps at
 *   `max-w-[80dvh]`); we use `100%` width + `100%` height (the host sizes us)
 *   and `100dvh`-safe min-heights, never `vh`.
 * - Option grids are a fixed 2-column grid (iOS parity — 4 options read as
 *   2×2, not 3+1). `minmax(0, 1fr)` lets the two columns shrink to fit so
 *   MCQ/Fill options never overflow at 320px nor over-stretch at 1280px.
 */
import type { CSSProperties } from "react";
import { colors, fontFamily } from "@/ui";

/** Full-bleed activity container. Lesson = bg #fafafa; feed = black. */
export function activityContainerStyle(isFeed: boolean): CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minHeight: 0,
    boxSizing: "border-box",
    padding: "20px 16px",
    backgroundColor: isFeed ? colors.black : colors.background,
    fontFamily,
    // Anchor for the lesson-variant CorrectDrawer (absolutely positioned).
    position: "relative",
  };
}

/** Left-aligned bold title (iOS xxlarge bold). Lesson dark / feed white. */
export function titleStyle(isFeed: boolean): CSSProperties {
  return {
    fontWeight: 700,
    fontSize: "1.5rem",
    lineHeight: 1.2,
    textAlign: "left",
    color: isFeed ? colors.white : colors.primary,
    marginBottom: 8,
  };
}

/** Centered title variant used by Match (iOS xlarge bold, centered). */
export function centeredTitleStyle(isFeed: boolean): CSSProperties {
  return {
    fontWeight: 700,
    fontSize: "1.25rem",
    lineHeight: 1.2,
    textAlign: "center",
    color: isFeed ? colors.white : colors.primary,
    marginBottom: 32,
  };
}

/**
 * Fixed 2-column option grid (iOS parity — 4 options are 2×2, never 3+1).
 * `minmax(0, 1fr)` lets the two columns shrink to fit so nothing overflows at
 * 320px while staying two-wide on a 600px phone card / desktop.
 */
export const buttonsGridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
  paddingTop: 12,
  justifyContent: "center",
};

/** Each grid cell holds one option button. */
export const gridCellStyle: CSSProperties = {
  display: "flex",
};
